import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  InputAdornment,
  Button,
  Chip,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  CircularProgress,
  Alert,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import {
  Search as SearchIcon,
  PlayArrow as PlayArrowIcon,
  PlaylistPlay as PlaylistPlayIcon,
  Schedule as ScheduleIcon,
} from "@mui/icons-material";
import {
  getTagsWithLastRun,
  runManualTags,
  processNewsPipeline,
  runLLMDeduplicationOnly,
} from "../services/NewsService";

const ManualRuns = () => {
  const theme = useTheme();

  // State management
  const [tagsData, setTagsData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("Active");
  const [selectedTags, setSelectedTags] = useState(new Set());

  // Sorting state
  const [orderBy, setOrderBy] = useState("tag");
  const [order, setOrder] = useState("asc");

  // Run modals
  const [runAllModalOpen, setRunAllModalOpen] = useState(false);
  const [runSelectedModalOpen, setRunSelectedModalOpen] = useState(false);
  const [runLLMOnlyModalOpen, setRunLLMOnlyModalOpen] = useState(false);
  const [passphrase, setPassphrase] = useState("");
  const [daysBack, setDaysBack] = useState(3);
  const [running, setRunning] = useState(false);

  // Load tags data
  const loadTagsData = async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await getTagsWithLastRun();
      console.log("Tags data loaded:", data);

      // Transform data to include last run date
      const transformedData = data.map((tag, index) => ({
        ...tag,
        lastRunDate: tag.last_run_date || "Never",
        isActive: tag.status === "active" || tag.status === "Active",
      }));

      setTagsData(transformedData);
    } catch (err) {
      setError(err.message);
      console.error("Error loading tags data:", err);
    } finally {
      setLoading(false);
    }
  };

  // Load data on component mount
  useEffect(() => {
    loadTagsData();
  }, []);

  // Get unique statuses for filter dropdown
  const uniqueStatuses = useMemo(() => {
    const statuses = [...new Set(tagsData.map((item) => item.status))].sort();
    return statuses;
  }, [tagsData]);

  // Sort data
  const getComparator = (order, orderBy) => {
    return order === "desc"
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  };

  const descendingComparator = (a, b, orderBy) => {
    let aValue = a[orderBy];
    let bValue = b[orderBy];

    // Handle null/undefined values
    if (aValue == null) aValue = "";
    if (bValue == null) bValue = "";

    // Special handling for date sorting
    if (orderBy === "lastRunDate") {
      // Treat "Never" as the earliest date
      if (aValue === "Never" && bValue === "Never") return 0;
      if (aValue === "Never") return 1;
      if (bValue === "Never") return -1;

      // Convert to dates for comparison
      const dateA = new Date(aValue);
      const dateB = new Date(bValue);
      return dateB - dateA;
    }

    // String comparison for other fields
    const aStr = String(aValue).toLowerCase();
    const bStr = String(bValue).toLowerCase();

    if (bStr < aStr) {
      return -1;
    }
    if (bStr > aStr) {
      return 1;
    }
    return 0;
  };

  // Filter and sort data
  const filteredData = useMemo(() => {
    let filtered = tagsData;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(
        (item) =>
          item.tag.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.target_ticker
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          item.buyer_ticker?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply status filter
    if (statusFilter) {
      filtered = filtered.filter((item) => item.status === statusFilter);
    }

    // Apply sorting
    const sorted = [...filtered].sort(getComparator(order, orderBy));
    return sorted;
  }, [tagsData, searchTerm, statusFilter, order, orderBy]);

  // Handle sort request
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  // Handle search
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  // Handle status filter
  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
  };

  // Handle tag selection
  const handleTagSelect = (tagId, isSelected) => {
    setSelectedTags((prev) => {
      const newSet = new Set(prev);
      if (isSelected) {
        newSet.add(tagId);
      } else {
        newSet.delete(tagId);
      }
      return newSet;
    });
  };

  // Handle select all
  const handleSelectAll = (isSelected) => {
    if (isSelected) {
      const allTagIds = filteredData.map((item) => item.id);
      setSelectedTags(new Set(allTagIds));
    } else {
      setSelectedTags(new Set());
    }
  };

  // Handle run all active tags
  const handleRunAllActive = () => {
    setRunAllModalOpen(true);
  };

  // Handle run selected tags
  const handleRunSelected = () => {
    if (selectedTags.size === 0) {
      setError("Please select at least one tag to run");
      return;
    }
    setRunSelectedModalOpen(true);
  };

  // Handle run LLM and deduplication only
  const handleRunLLMOnly = () => {
    if (selectedTags.size === 0) {
      setError("Please select at least one tag to run");
      return;
    }
    setRunLLMOnlyModalOpen(true);
  };

  // Handle run confirmation
  const handleRunConfirm = async (isRunAll = false) => {
    if (passphrase !== "Glazer123") {
      setError("Invalid passphrase. Please enter the correct passphrase.");
      return;
    }

    try {
      setRunning(true);
      setError(null);

      let result;

      if (isRunAll) {
        // Run the complete news processing pipeline for all active tags
        console.log("Running complete news processing pipeline");
        result = await processNewsPipeline();
        console.log("Pipeline result:", result);
      } else {
        // Run selected tags only
        const tagsToRun = Array.from(selectedTags);
        console.log("Running selected tags:", tagsToRun);
        result = await runManualTags(tagsToRun, passphrase, daysBack);
        console.log("Selected tags result:", result);
      }

      // Close modals and reset
      setRunAllModalOpen(false);
      setRunSelectedModalOpen(false);
      setRunLLMOnlyModalOpen(false);
      setPassphrase("");
      setDaysBack(3);
      setSelectedTags(new Set());

      // Show success message
      setError(null);
      console.log("Tags run successfully");

      // Reload data to update last run dates
      await loadTagsData();
    } catch (err) {
      setError(err.message);
      console.error("Error running tags:", err);
    } finally {
      setRunning(false);
    }
  };

  // Handle LLM and deduplication only confirmation
  const handleRunLLMOnlyConfirm = async () => {
    if (passphrase !== "Glazer123") {
      setError("Invalid passphrase. Please enter the correct passphrase.");
      return;
    }

    try {
      setRunning(true);
      setError(null);

      const tagsToRun = Array.from(selectedTags);
      console.log(
        "Running LLM and deduplication only for selected tags:",
        tagsToRun
      );
      const result = await runLLMDeduplicationOnly(
        tagsToRun,
        passphrase,
        daysBack
      );
      console.log("LLM only result:", result);

      // Close modal and reset
      setRunLLMOnlyModalOpen(false);
      setPassphrase("");
      setDaysBack(3);
      setSelectedTags(new Set());

      // Show success message
      setError(null);
      console.log("LLM and deduplication run successfully");

      // Reload data to update last run dates
      await loadTagsData();
    } catch (err) {
      setError(err.message);
      console.error("Error running LLM and deduplication only:", err);
    } finally {
      setRunning(false);
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString || dateString === "Never") {
      return "Never";
    }
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "active":
        return "success";
      case "inactive":
        return "error";
      case "pending":
        return "warning";
      default:
        return "default";
    }
  };

  if (loading && tagsData.length === 0) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Header with buttons and filters */}
      <Paper
        elevation={1}
        sx={{
          p: 2,
          mb: 2,
          borderRadius: 1,
          flexShrink: 0,
        }}
      >
        <Box
          display="flex"
          flexDirection={{ xs: "column", sm: "row" }}
          gap={2}
          alignItems={{ xs: "stretch", sm: "center" }}
          mb={2}
        >
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              onChange={handleStatusFilterChange}
              label="Status"
            >
              <MenuItem value="">All Status</MenuItem>
              {uniqueStatuses.map((status) => (
                <MenuItem key={status} value={status}>
                  {status}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            placeholder="Search tags, tickers..."
            value={searchTerm}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
            sx={{ flex: 1, minWidth: 200 }}
            size="small"
          />
        </Box>

        <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
          <Button
            variant="contained"
            startIcon={<PlayArrowIcon />}
            onClick={handleRunAllActive}
            disabled={running}
            sx={{
              backgroundColor: "#4caf50",
              "&:hover": {
                backgroundColor: "#45a049",
              },
            }}
          >
            Run All Active Tags
          </Button>

          <Button
            variant="contained"
            startIcon={<PlaylistPlayIcon />}
            onClick={handleRunSelected}
            disabled={running || selectedTags.size === 0}
            sx={{
              backgroundColor: theme.palette.primary.main,
              "&:hover": {
                backgroundColor: theme.palette.primary.dark,
              },
            }}
          >
            Run Selected Tags ({selectedTags.size})
          </Button>

          <Button
            variant="contained"
            startIcon={<PlaylistPlayIcon />}
            onClick={handleRunLLMOnly}
            disabled={running || selectedTags.size === 0}
            sx={{
              backgroundColor: "#ff9800",
              "&:hover": {
                backgroundColor: "#f57c00",
              },
            }}
          >
            Run LLM & Deduplication Only ({selectedTags.size})
          </Button>

          <Typography variant="body2" color="text.secondary">
            {filteredData.filter((item) => item.isActive).length} active tags
          </Typography>
        </Box>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert
          severity={error.includes("successfully") ? "success" : "error"}
          sx={{ mb: 2, flexShrink: 0 }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {/* Table */}
      <Paper
        elevation={1}
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <TableContainer
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            overflowX: "auto",
            "&::-webkit-scrollbar": {
              width: "8px",
              height: "8px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: theme.palette.grey[100],
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: theme.palette.grey[400],
              borderRadius: "4px",
              "&:hover": {
                backgroundColor: theme.palette.grey[500],
              },
            },
          }}
        >
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" sx={{ py: 0.5 }}>
                  <Checkbox
                    size="small"
                    indeterminate={
                      selectedTags.size > 0 &&
                      selectedTags.size < filteredData.length
                    }
                    checked={
                      filteredData.length > 0 &&
                      selectedTags.size === filteredData.length
                    }
                    onChange={(e) => handleSelectAll(e.target.checked)}
                  />
                </TableCell>
                <TableCell sx={{ py: 0.5 }}>
                  <TableSortLabel
                    active={orderBy === "tag"}
                    direction={orderBy === "tag" ? order : "asc"}
                    onClick={() => handleRequestSort("tag")}
                  >
                    Tag
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ py: 0.5 }}>
                  <TableSortLabel
                    active={orderBy === "target_ticker"}
                    direction={orderBy === "target_ticker" ? order : "asc"}
                    onClick={() => handleRequestSort("target_ticker")}
                  >
                    Target Ticker
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ py: 0.5 }}>
                  <TableSortLabel
                    active={orderBy === "buyer_ticker"}
                    direction={orderBy === "buyer_ticker" ? order : "asc"}
                    onClick={() => handleRequestSort("buyer_ticker")}
                  >
                    Buyer Ticker
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ py: 0.5 }}>
                  <TableSortLabel
                    active={orderBy === "status"}
                    direction={orderBy === "status" ? order : "asc"}
                    onClick={() => handleRequestSort("status")}
                  >
                    Status
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ py: 0.5 }}>
                  <TableSortLabel
                    active={orderBy === "lastRunDate"}
                    direction={orderBy === "lastRunDate" ? order : "asc"}
                    onClick={() => handleRequestSort("lastRunDate")}
                  >
                    Last Run
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ py: 0.5 }}>Company Keywords</TableCell>
                <TableCell sx={{ py: 0.5 }}>Generic Keywords</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredData.map((item) => (
                <TableRow key={item.id} hover sx={{ "& td": { py: 0.5 } }}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={selectedTags.has(item.id)}
                      onChange={(e) =>
                        handleTagSelect(item.id, e.target.checked)
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={item.tag}
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ height: "20px", fontSize: "0.7rem" }}
                    />
                  </TableCell>
                  <TableCell sx={{ fontSize: "0.875rem" }}>
                    {item.target_ticker || "N/A"}
                  </TableCell>
                  <TableCell sx={{ fontSize: "0.875rem" }}>
                    {item.buyer_ticker || "N/A"}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={item.status || "Unknown"}
                      size="small"
                      color={getStatusColor(item.status)}
                      variant="filled"
                      sx={{ height: "20px", fontSize: "0.7rem" }}
                    />
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={0.3}>
                      <ScheduleIcon
                        fontSize="small"
                        color="action"
                        sx={{ fontSize: "0.875rem" }}
                      />
                      <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
                        {formatDate(item.lastRunDate)}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={{
                      maxWidth: 200,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      fontSize: "0.875rem",
                    }}
                    title={item.company_keywords}
                  >
                    {item.company_keywords || "N/A"}
                  </TableCell>
                  <TableCell
                    sx={{
                      maxWidth: 200,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      fontSize: "0.875rem",
                    }}
                    title={item.generic_keywords}
                  >
                    {item.generic_keywords || "N/A"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Run All Modal */}
      <Dialog
        open={runAllModalOpen}
        onClose={() => setRunAllModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Run All Active Tags</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            This will run the complete news processing pipeline for all active
            tags. This includes getting company news, LLM classification, and
            deduplication. Please enter the passphrase to confirm.
          </Typography>
          <TextField
            fullWidth
            label="Passphrase"
            type="password"
            value={passphrase}
            onChange={(e) => setPassphrase(e.target.value)}
            margin="normal"
            placeholder="Enter passphrase"
          />
          <TextField
            fullWidth
            label="Days Back"
            type="number"
            value={daysBack}
            onChange={(e) => setDaysBack(parseInt(e.target.value) || 3)}
            margin="normal"
            inputProps={{ min: 1, max: 30 }}
            helperText="Number of days to look back for news (1-30)"
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Active tags to be run:{" "}
            {filteredData.filter((item) => item.isActive).length}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRunAllModalOpen(false)}>Cancel</Button>
          <Button
            onClick={() => handleRunConfirm(true)}
            variant="contained"
            disabled={running || !passphrase}
          >
            {running ? <CircularProgress size={20} /> : "Run All Active"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Run Selected Modal */}
      <Dialog
        open={runSelectedModalOpen}
        onClose={() => setRunSelectedModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Run Selected Tags</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            This will run the news processing pipeline for the selected tags.
            Please enter the passphrase to confirm.
          </Typography>
          <TextField
            fullWidth
            label="Passphrase"
            type="password"
            value={passphrase}
            onChange={(e) => setPassphrase(e.target.value)}
            margin="normal"
            placeholder="Enter passphrase"
          />
          <TextField
            fullWidth
            label="Days Back"
            type="number"
            value={daysBack}
            onChange={(e) => setDaysBack(parseInt(e.target.value) || 3)}
            margin="normal"
            inputProps={{ min: 1, max: 30 }}
            helperText="Number of days to look back for news (1-30)"
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Selected tags to be run: {selectedTags.size}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRunSelectedModalOpen(false)}>Cancel</Button>
          <Button
            onClick={() => handleRunConfirm(false)}
            variant="contained"
            disabled={running || !passphrase}
          >
            {running ? <CircularProgress size={20} /> : "Run Selected"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Run LLM & Deduplication Only Modal */}
      <Dialog
        open={runLLMOnlyModalOpen}
        onClose={() => setRunLLMOnlyModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Run LLM & Deduplication Only</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            This will run LLM classification and deduplication only for the
            selected tags. It will NOT fetch new headlines - it will process
            existing headlines from the database based on the number of days
            back specified. Please enter the passphrase to confirm.
          </Typography>
          <TextField
            fullWidth
            label="Passphrase"
            type="password"
            value={passphrase}
            onChange={(e) => setPassphrase(e.target.value)}
            margin="normal"
            placeholder="Enter passphrase"
          />
          <TextField
            fullWidth
            label="Days Back"
            type="number"
            value={daysBack}
            onChange={(e) => setDaysBack(parseInt(e.target.value) || 3)}
            margin="normal"
            inputProps={{ min: 1, max: 30 }}
            helperText="Number of days to look back for headlines (1-30)"
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Selected tags to be processed: {selectedTags.size}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRunLLMOnlyModalOpen(false)}>Cancel</Button>
          <Button
            onClick={handleRunLLMOnlyConfirm}
            variant="contained"
            disabled={running || !passphrase}
            sx={{
              backgroundColor: "#ff9800",
              "&:hover": {
                backgroundColor: "#f57c00",
              },
            }}
          >
            {running ? (
              <CircularProgress size={20} />
            ) : (
              "Run LLM & Deduplication Only"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ManualRuns;
